/**
 * K-002 — service worker registration and the update handshake (§20).
 *
 * The update strategy is deliberate: a new bundle waits instead of taking
 * over. Swapping the running app while a participant is mid-submission would
 * reload the page out from under them, and the outbox record they just wrote
 * would have to be recovered rather than simply finished. The page decides
 * when the swap is safe and calls `applyUpdate()`.
 */

const SERVICE_WORKER_URL = '/sw.js';

export interface ServiceWorkerHandle {
  /** Tell the waiting worker to take over, then reload once it has. */
  applyUpdate: () => void;
}

export function registerServiceWorker(onUpdateReady: () => void): ServiceWorkerHandle {
  const handle: ServiceWorkerHandle = {
    applyUpdate: () => {
      void navigator.serviceWorker.getRegistration().then((registration) => {
        registration?.waiting?.postMessage('SKIP_WAITING');
      });
    },
  };

  if (!('serviceWorker' in navigator)) return handle;

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Guarded: Chrome fires this again if the worker is replaced twice, and a
    // reload loop at a check-in desk is worse than a stale bundle.
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  void navigator.serviceWorker.register(SERVICE_WORKER_URL).then((registration) => {
    // A worker already waiting from a previous visit.
    if (registration.waiting && navigator.serviceWorker.controller) onUpdateReady();

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;

      installing.addEventListener('statechange', () => {
        // `controller` is null on the very first install, which is not an
        // update and must not prompt anyone.
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          onUpdateReady();
        }
      });
    });
  });

  return handle;
}
